import { ObstacleTileStage, type TfObstructionProximityFadeSettings } from '@nova/types';

const OBSTACLE_TILE_STAGE_BY_NAME: Readonly<Record<string, ObstacleTileStage>> = {
  none: ObstacleTileStage.None,
  sprout: ObstacleTileStage.Sprout,
  sapling: ObstacleTileStage.Sapling,
  tree: ObstacleTileStage.Tree
};

function isConfigRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isObstacleTileStageInteger(value: number): value is ObstacleTileStage {
  return Number.isInteger(value) && value >= 0 && value <= ObstacleTileStage.Tree;
}

/** Parses an {@link ObstacleTileStage} from a JSON string name or enum number. */
export function parseObstacleTileStageConfig(value: unknown, label: string): ObstacleTileStage {
  if (typeof value === 'number' && isObstacleTileStageInteger(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const stage = OBSTACLE_TILE_STAGE_BY_NAME[value.trim().toLowerCase()];
    if (stage !== undefined) {
      return stage;
    }
  }

  throw new Error(`${label} must be an ObstacleTileStage name or integer 0–3`);
}

/** Parses {@link TfObstructionProximityFadeSettings} from engine JSON or bootstrap wire. */
export function parseObstructionProximityFadeSettings(value: unknown, label: string): TfObstructionProximityFadeSettings {
  if (!isConfigRecord(value)) {
    throw new Error(`${label} must be an object`);
  }

  const { enabled, radiusTiles, centerAlpha, applyForAllyBots, stages } = value;

  if (typeof enabled !== 'boolean') {
    throw new Error(`${label}.enabled must be a boolean`);
  }

  if (typeof radiusTiles !== 'number' || !Number.isFinite(radiusTiles) || radiusTiles < 0) {
    throw new Error(`${label}.radiusTiles must be a non-negative finite number`);
  }

  if (typeof centerAlpha !== 'number' || !Number.isFinite(centerAlpha) || centerAlpha < 0 || centerAlpha > 1) {
    throw new Error(`${label}.centerAlpha must be a finite number in [0, 1]`);
  }

  if (typeof applyForAllyBots !== 'boolean') {
    throw new Error(`${label}.applyForAllyBots must be a boolean`);
  }

  if (!Array.isArray(stages)) {
    throw new Error(`${label}.stages must be an array`);
  }

  return {
    enabled,
    radiusTiles,
    centerAlpha,
    applyForAllyBots,
    stages: stages.map((stage, index) => parseObstacleTileStageConfig(stage, `${label}.stages[${index}]`))
  };
}
